/**
 * Automation builder — the centered modal (monday-style, never full-width).
 *
 * Left: the canvas — a recessed workbench where auto-labeled step cards you
 * read like a sentence (Trigger → Only if → Then do this → And) dock onto
 * one continuous rail running down the left gutter. The rail carries a node
 * per card (filled at the trigger, the flow's origin; lit on the selected
 * card) and holds the "+" insert control in each gap.
 * Right: the setup panel for whichever card is selected (sections →
 * labels → controls; multi-channel chips; exact send time; message tokens).
 *
 * Cards animate in down the rail on open (and when inserted), staggered by
 * their position via the --gmba-i custom property. That is the one authored
 * motion moment here; everything else is state feedback. All of it sits
 * behind prefers-reduced-motion in automations.css.
 *
 * Split across this folder: helpers.js (pure validation/id/token-insert
 * helpers), StepPicker.jsx (the trigger/step search dropdown),
 * SetupControls.jsx (the individual field controls — select, people picker,
 * channel chips, message editor), SetupPanel.jsx (assembles those controls
 * per selected step), KebabMenu.jsx (the ⋯ menu shared by trigger/step
 * cards). This file is just the canvas + top bar + the state that ties it
 * all together.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import { UseCompanyKpis } from "../../../hooks/kpi";
import { UseCompanyWorkspaces, UseUsersFullData } from "../../../hooks/company";
import { getMasterBadges } from "../../../redux/badges/actions";
import { CONDITIONS, triggerById, stepDefById, stepTag } from "../catalog";
import useOutsideClick, { keyActivate } from "../useOutsideClick";
import brandTokens from "../brandTokens";
import "../automations.css";

import { validateDraft, genId } from "./helpers";
import StepPicker from "./StepPicker";
import SetupPanel from "./SetupPanel";
import KebabMenu, { MI } from "./KebabMenu";
import { IconClose, IconMore, IconChevron, IconPlus, IconBolt } from "./icons";

const freshDraft = () => ({ name: "", active: true, trigger: null, steps: [] });

export default function BuilderModal({
  open,
  automation = null, // null → creating
  workspaceName = "",
  groupId = null,
  onSave, // async (payload, isNew) => saved automation
  onClose,
  primary = "#1B598E",
}) {
  const dispatch = useDispatch();
  const companyKpis = UseCompanyKpis();
  const workspaces = UseCompanyWorkspaces(true);
  const masterBadges = useSelector((state) => state.masterBadgeReducer?.masterBadges || []);

  const workspaceNameById = useMemo(() => {
    const map = new Map();
    (workspaces || []).forEach((w) => {
      if (w?._id) map.set(String(w._id), w.name || "Workspace");
    });
    return map;
  }, [workspaces]);

  const companyGroupIds = useMemo(
    () => new Set([...(workspaceNameById.keys())]),
    [workspaceNameById]
  );

  const kpiOptions = useMemo(() => {
    let rows = [];
    if (Array.isArray(companyKpis)) {
      rows = companyKpis;
    } else if (companyKpis && typeof companyKpis === "object") {
      if (groupId && Array.isArray(companyKpis[groupId])) {
        rows = companyKpis[groupId];
      } else {
        rows = Object.values(companyKpis).flat();
      }
    }
    const seen = new Set();
    return rows
      .map((k) => ({
        value: String(k._id || k.id || ""),
        label: k.title || k.name || "Untitled metric",
      }))
      .filter((o) => {
        if (!o.value || seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [companyKpis, groupId]);

  const badgeOptions = useMemo(() => {
    const rows = Array.isArray(masterBadges) ? masterBadges : [];
    // Dev has no badges today — show every badge in the company, and label
    // ones from other workspaces so the pick is clear.
    const scoped = rows.filter((b) => {
      const bid = String(b.groupId?._id || b.groupId || "");
      if (!bid) return true;
      if (!companyGroupIds.size) return true;
      return companyGroupIds.has(bid);
    });
    const seen = new Set();
    return scoped
      .map((b) => {
        const bid = String(b.groupId?._id || b.groupId || "");
        const title = b.title || "Untitled badge";
        const ws = bid ? workspaceNameById.get(bid) : "";
        const fromOther = bid && groupId && bid !== String(groupId);
        return {
          value: String(b._id || ""),
          label: fromOther && ws ? `${title} (${ws})` : title,
          _sameWorkspace: !bid || !groupId || bid === String(groupId),
        };
      })
      .filter((o) => {
        if (!o.value || seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      })
      .sort((a, b) => {
        if (a._sameWorkspace !== b._sameWorkspace) return a._sameWorkspace ? -1 : 1;
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label }) => ({ value, label }));
  }, [masterBadges, groupId, companyGroupIds, workspaceNameById]);

  const [draft, setDraft] = useState(freshDraft());
  const [selected, setSelected] = useState(null); // 'trigger' | step.id | null
  const [picker, setPicker] = useState(null); // {mode:'trigger'} | {mode:'step', index}
  const [openSelect, setOpenSelect] = useState(null);
  const [kebab, setKebab] = useState(null); // 'trigger' | step.id
  const [renaming, setRenaming] = useState(null); // 'trigger' | step.id
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null); // saved automation shown in the success modal
  const [confirmClose, setConfirmClose] = useState(false);
  const dirtyRef = useRef(false);

  // Active players sort first (the common case for who to notify); inactive
  // ones are still pickable — e.g. re-engaging someone or an automation that
  // already targets them — but grouped and labeled so it's never ambiguous
  // which is which.
  const groupUsers = (rows) => {
    const label = (u) => ({
      value: u.value,
      label: u.label,
      isActive: u.isActive,
      isAdmin: u.isAdmin,
      hasPhone: u.hasPhone,
    });
    const active = rows.filter((u) => u.isActive).map((u) => ({ ...label(u), group: "Active" }));
    const inactive = rows.filter((u) => !u.isActive).map((u) => ({ ...label(u), group: "Inactive" }));
    return [...active, ...inactive];
  };

  // For "notify a specific person" (and narrowing "notify their manager") —
  // this workspace's players plus every company admin, from the same company user
  // list Player Management reads. (The by-group endpoint isn't scoped to the
  // company, so it can return people who don't belong to it.) Archived
  // people are excluded — they're gone, not just inactive.
  const companyUsers = UseUsersFullData();
  const workspaceMembers = useMemo(() => {
    if (!open || !groupId) return [];
    return (companyUsers || [])
      // Admins from any workspace in the company are selectable (so a manager
      // from another workspace can be notified here); regular players stay
      // limited to this workspace.
      .filter(
        (u) =>
          !u.isArchived &&
          (String(u.groupId) === String(groupId) || Boolean(u.isAdmin))
      )
      .map((u) => ({
        value: u.userId,
        label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
        isAdmin: Boolean(u.isAdmin),
        isActive: u.isActive !== false,
        // Drives the "won't get the SMS" notice on notify steps. Exempt
        // players are treated as reachable-enough here — they've been
        // deliberately opted out of the phone requirement.
        hasPhone: Boolean(u.phoneNumber) || u.phoneExempt === true,
      }));
  }, [open, groupId, companyUsers]);

  const workspaceUsers = useMemo(() => groupUsers(workspaceMembers), [workspaceMembers]);

  // (Re)hydrate whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    dispatch(getMasterBadges());
    if (automation) {
      setDraft({
        name: automation.name || "",
        active: automation.active !== false,
        trigger: automation.trigger ? { type: automation.trigger.type, config: { ...(automation.trigger.config || {}) } } : null,
        // Conditions first, actions after — a stable sort so any automation
        // saved before this ordering existed displays exactly like the
        // engine actually runs it (see addStep's comment).
        steps: (automation.steps || [])
          .map((s) => ({ ...s, config: { ...(s.config || {}) } }))
          .sort((a, b) => {
            if (a.kind === b.kind) return 0;
            return a.kind === "condition" ? -1 : 1;
          }),
      });
      setSelected(automation.trigger ? "trigger" : null);
    } else {
      setDraft(freshDraft());
      setSelected(null);
      setPicker(null); // fresh canvas: the chooser card invites, nothing auto-opens
    }
    setSuccess(null);
    setOpenSelect(null);
    setKebab(null);
    setRenaming(null);
    dirtyRef.current = false;
  }, [open, automation, dispatch]);

  // Entrance choreography gate. The staggered keyframes hold opacity:0 during
  // their delay, so they must never be the resting state — if they didn't run
  // the canvas would stay blank. Keeping them behind a class that is always
  // cleared shortly after open means the visible layout is what the CSS falls
  // back to. The window covers the longest stagger plus the 220ms rise.
  const [entering, setEntering] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntering(false);
      return undefined;
    }
    setEntering(true);
    const t = setTimeout(() => setEntering(false), 700);
    return () => clearTimeout(t);
  }, [open, automation]);

  const mutate = (fn) => {
    dirtyRef.current = true;
    setDraft((d) => fn(structuredClone(d)));
  };

  // Any click outside the picker (or Escape) closes it — the picker itself
  // stops propagation, and the buttons that open it toggle instead.
  useOutsideClick(!!picker, () => setPicker(null));

  const updateConfig = (target, keyOrPatch, value) => {
    mutate((d) => {
      const cfg =
        target === "trigger"
          ? d.trigger?.config
          : d.steps.find((s) => s.id === target)?.config;
      if (!cfg) return d;
      if (keyOrPatch && typeof keyOrPatch === "object" && !Array.isArray(keyOrPatch)) {
        Object.assign(cfg, keyOrPatch);
      } else {
        cfg[keyOrPatch] = value;
      }
      return d;
    });
  };

  const pickTrigger = (def) => {
    mutate((d) => {
      // If the name is still the previous trigger's auto-fill (user never
      // typed their own), keep it in sync with the new trigger.
      const prevDef = d.trigger ? triggerById(d.trigger.type) : null;
      const nameUntouched = !d.name || (prevDef && d.name === prevDef.label);
      d.trigger = { type: def.id, config: { ...def.defaults } };
      if (nameUntouched) d.name = def.label;
      return d;
    });
    setPicker(null);
    setSelected("trigger");
  };

  const addStep = (def, index) => {
    const isCondition = CONDITIONS.some((c) => c.id === def.id);
    const isFirstAction = def && !isCondition && !draft.steps.some((s) => s.kind === "action");
    // notify_player/notify_manager's defaults read the current trigger so a
    // "hits a target" automation doesn't default to concerned-sounding copy.
    const defaults = typeof def.defaults === "function" ? def.defaults(draft?.trigger?.type) : def.defaults;
    const step = {
      id: genId(),
      kind: isCondition ? "condition" : "action",
      type: def.id,
      config: { ...defaults },
      disabled: false,
    };
    mutate((d) => {
      const conditionCount = d.steps.filter((s) => s.kind === "condition").length;
      // The engine checks every condition before running any action,
      // regardless of where each one sits in the array (automationEngine.js
      // runSteps) — keeping conditions contiguous and first on the canvas
      // too means the card order can never visually misrepresent what
      // actually runs.
      const at = isCondition ? conditionCount : Math.max(index == null ? d.steps.length : index, conditionCount);
      d.steps.splice(at, 0, step);
      return d;
    });
    setPicker(null);
    setSelected(step.id);
    if (isFirstAction) toast("Now it actually does something 👏", { duration: 2400 });
  };

  const stepOps = (step, index) => ({
    duplicate: () => {
      mutate((d) => {
        d.steps.splice(index + 1, 0, { ...structuredClone(step), id: genId() });
        return d;
      });
    },
    move: (dir) => {
      mutate((d) => {
        const to = index + dir;
        if (to < 0 || to >= d.steps.length) return d;
        // Never move a step across the condition/action boundary — see
        // addStep's comment on why that order is never just cosmetic here.
        if (d.steps[to].kind !== d.steps[index].kind) return d;
        const [s] = d.steps.splice(index, 1);
        d.steps.splice(to, 0, s);
        return d;
      });
    },
    toggleOff: () => {
      mutate((d) => {
        const s = d.steps.find((x) => x.id === step.id);
        if (s) s.disabled = !s.disabled;
        return d;
      });
    },
    remove: () => {
      mutate((d) => {
        d.steps = d.steps.filter((x) => x.id !== step.id);
        return d;
      });
      if (selected === step.id) setSelected(null);
    },
    canMoveUp: index > 0 && draft.steps[index - 1].kind === step.kind,
    canMoveDown: index < draft.steps.length - 1 && draft.steps[index + 1].kind === step.kind,
  });

  const issues = useMemo(() => validateDraft(draft), [draft]);
  const canSave =
    !!draft.trigger && draft.steps.some((s) => s.kind === "action" && !s.disabled) && issues.length === 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim() || "Untitled automation",
        active: draft.active,
        trigger: draft.trigger,
        steps: draft.steps,
      };
      const isNew = !automation?._id;
      const saved = await onSave(payload, isNew);
      dirtyRef.current = false;
      if (isNew) {
        setSuccess(saved || payload);
      } else {
        toast.success("Automation saved.");
        onClose();
      }
    } catch (err) {
      toast.error(err?.message || "Could not save the automation.");
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (dirtyRef.current && !success) setConfirmClose(true);
    else onClose();
  };

  /* ------------------------------ canvas ------------------------------ */

  const triggerDef = draft.trigger ? triggerById(draft.trigger.type) : null;
  const iconClassFor = (target) => {
    if (target === "trigger") return "gmba-blue";
    const step = draft.steps.find((s) => s.id === target);
    if (!step) return "gmba-blue";
    if (step.kind === "condition") return "gmba-amber";
    const actionIdx = draft.steps.filter((s) => s.kind === "action").findIndex((s) => s.id === step.id);
    return actionIdx % 2 === 0 ? "gmba-green2" : "gmba-violet";
  };

  const renameInput = (current, commit) => (
    <input
      className="gmba-textin"
      style={{ padding: "4px 8px", fontSize: 13.5, fontWeight: 600 }}
      ref={(el) => el && el.focus()}
      defaultValue={current}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(e.target.value);
        if (e.key === "Escape") setRenaming(null);
      }}
      onBlur={(e) => commit(e.target.value)}
    />
  );

  /**
   * Entrance stagger. The flow assembles top-to-bottom down the rail, so each
   * row gets a position index the CSS turns into an animation-delay. Indices
   * are derived (not a running counter) so a re-render can never reshuffle
   * them: 0 hello, 1 stage, 2 trigger, then two rows per step, then the tail.
   */
  const stagger = (i) => ({ "--gmba-i": i });
  const stepRowIndex = (index) => 3 + index * 2;
  const tailRowIndex = 3 + draft.steps.length * 2;

  const confetti = useMemo(() => {
    const colors = ["#1B598E", "#12B76A", "#F79009", "#7A5AF8", "#EE46BC", "#06AED4"];
    return Array.from({ length: 16 }, (_, i) => ({
      id: `c${i}`,
      left: `${6 + ((i * 37) % 88)}%`,
      background: colors[i % colors.length],
      animationDelay: `${(i % 7) * 0.05}s`,
    }));
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 1080,
            maxWidth: "96vw",
            height: "min(88vh, 720px)",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // A deeper, softer lift than the theme default — the modal has to
            // read as clearly above the page for its own canvas to feel
            // recessed rather than just gray.
            boxShadow: "0 24px 48px -12px rgba(20,26,38,.22), 0 48px 96px -24px rgba(20,26,38,.24)",
          },
        }}
        BackdropProps={{ sx: { backgroundColor: "rgba(20,26,38,.44)", backdropFilter: "blur(3px)" } }}
      >
        <div className="gmba" style={{ ...brandTokens(primary), display: "flex", flexDirection: "column", height: "100%" }}>
          {/* top bar */}
          <div className="gmba-top">
            <span className="gmba-name">
              <input
                value={draft.name}
                placeholder="Untitled automation"
                onChange={(e) => mutate((d) => { d.name = e.target.value; return d; })}
              />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
            </span>
            {workspaceName && <span className="gmba-chip">{workspaceName}</span>}
            <span className="gmba-spacer" />
            <span className="gmba-active">
              Active
              <button
                type="button"
                className={`gmba-sw ${draft.active ? "gmba-on" : "gmba-offsw"}`}
                onClick={() => mutate((d) => { d.active = !d.active; return d; })}
                aria-label="Toggle active"
              />
            </span>
            {issues.length > 0 && draft.trigger && (
              <span className="gmba-savehint">{issues.length} thing{issues.length === 1 ? "" : "s"} to fix</span>
            )}
            <button type="button" className="gmba-btn gmba-ghost" onClick={requestClose}>Cancel</button>
            <button type="button" className="gmba-btn gmba-primary" disabled={!canSave || saving} onClick={handleSave}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="gmba-x" onClick={requestClose} aria-label="Close">
              <IconClose />
            </button>
          </div>

          <div className="gmba-body">
            {/* canvas */}
            <div className="gmba-canvas" onClick={() => { setPicker(null); setOpenSelect(null); }}>
              <div className={`gmba-flow${entering ? " gmba-entering" : ""}`}>
                {/* The rail every card docks onto. Purely structural, so it
                    sits behind the cards and takes no pointer events. */}
                <span className="gmba-rail" aria-hidden="true" />

                {!draft.trigger && (
                  <div className="gmba-hello" style={stagger(0)}>New automation — <b>start by choosing what kicks it off.</b></div>
                )}
                <div className="gmba-stage" style={stagger(1)}>When this happens</div>

                {/* trigger card / chooser */}
                <div className="gmba-cardwrap gmba-origin" style={stagger(2)}>
                  {draft.trigger && triggerDef ? (
                    <div
                      className={`gmba-card${selected === "trigger" ? " gmba-sel" : ""}`}
                      onClick={(e) => { e.stopPropagation(); setSelected("trigger"); setPicker(null); }}
                      {...keyActivate(() => { setSelected("trigger"); setPicker(null); })}
                    >
                      <span className="gmba-ic gmba-blue">{triggerDef.icon}</span>
                      <span className="gmba-ctext">
                        <span className="gmba-ctag">Trigger</span>
                        {renaming === "trigger" ? (
                          renameInput(draft.trigger.config.customLabel || triggerDef.label, (v) => {
                            mutate((d) => { d.trigger.config.customLabel = v.trim() || undefined; return d; });
                            setRenaming(null);
                          })
                        ) : (
                          <span className="gmba-ct">
                            {draft.trigger.config.customLabel || triggerDef.label}
                            {issues.some((i) => i.target === "trigger") && <span className="gmba-warndot" title="Needs attention" />}
                          </span>
                        )}
                        <span className="gmba-cd">{triggerDef.summary(draft.trigger.config)}</span>
                      </span>
                      <button
                        type="button"
                        className="gmba-kebab"
                        aria-label="Trigger options"
                        onClick={(e) => { e.stopPropagation(); setKebab(kebab === "trigger" ? null : "trigger"); }}
                      ><IconMore /></button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="gmba-chooser"
                      onClick={(e) => { e.stopPropagation(); setPicker(picker?.mode === "trigger" ? null : { mode: "trigger" }); }}
                    >
                      <span className="gmba-ic gmba-blue"><IconBolt /></span>
                      <span className="gmba-ctext">
                        <span className="gmba-ctag">Trigger</span>
                        <span className="gmba-ct">Choose a trigger</span>
                      </span>
                      <span className="gmba-cv"><IconChevron /></span>
                    </button>
                  )}
                  {kebab === "trigger" && (
                    <KebabMenu
                      onClose={() => setKebab(null)}
                      items={[
                        { label: "Rename", icon: MI.rename, onClick: () => setRenaming("trigger") },
                        { label: "Change trigger", icon: MI.swap, onClick: () => setPicker({ mode: "trigger" }) },
                      ]}
                    />
                  )}
                  {picker?.mode === "trigger" && <StepPicker mode="trigger" onPick={pickTrigger} inline />}
                </div>

                {/* steps */}
                {draft.steps.map((step, index) => {
                  const def = stepDefById(step.type);
                  if (!def) return null;
                  const ops = stepOps(step, index);
                  const tag = stepTag(draft.steps, index);
                  return (
                    <React.Fragment key={step.id}>
                      <div className="gmba-conn" style={stagger(stepRowIndex(index))}>
                        {draft.trigger && (
                          <button
                            type="button"
                            className="gmba-plus"
                            title="Insert a step here"
                            aria-label="Insert a step here"
                            onClick={(e) => {
                              e.stopPropagation();
                              const isOpen = picker?.mode === "step" && picker.index === index;
                              setPicker(isOpen ? null : { mode: "step", index });
                            }}
                          ><IconPlus /></button>
                        )}
                      </div>
                      {picker?.mode === "step" && picker.index === index && (
                        <div className="gmba-cardwrap gmba-nonode" style={{ marginBottom: 10 }}>
                          <StepPicker mode="step" onPick={(d) => addStep(d, index)} inline />
                        </div>
                      )}
                      <div className="gmba-cardwrap" style={stagger(stepRowIndex(index) + 1)}>
                        <div
                          className={`gmba-card${selected === step.id ? " gmba-sel" : ""}${step.disabled ? " gmba-stepoff" : ""}`}
                          onClick={(e) => { e.stopPropagation(); setSelected(step.id); setPicker(null); }}
                          {...keyActivate(() => { setSelected(step.id); setPicker(null); })}
                        >
                          <span className={`gmba-ic ${iconClassFor(step.id)}`}>{def.icon}</span>
                          <span className="gmba-ctext">
                            <span className="gmba-ctag">{step.disabled ? `${tag} · off` : tag}</span>
                            {renaming === step.id ? (
                              renameInput(step.label || def.label, (v) => {
                                mutate((d) => {
                                  const s = d.steps.find((x) => x.id === step.id);
                                  if (s) s.label = v.trim() || undefined;
                                  return d;
                                });
                                setRenaming(null);
                              })
                            ) : (
                              <span className="gmba-ct">
                                {step.label || def.label}
                                {!step.disabled && issues.some((i) => i.target === step.id) && (
                                  <span className="gmba-warndot" title="Needs attention" />
                                )}
                              </span>
                            )}
                            <span className="gmba-cd">{def.summary(step.config || {})}</span>
                          </span>
                          <button
                            type="button"
                            className="gmba-kebab"
                            aria-label={`Options for ${step.label || def.label}`}
                            onClick={(e) => { e.stopPropagation(); setKebab(kebab === step.id ? null : step.id); }}
                          ><IconMore /></button>
                        </div>
                        {kebab === step.id && (
                          <KebabMenu
                            onClose={() => setKebab(null)}
                            items={[
                              { label: "Rename", icon: MI.rename, onClick: () => setRenaming(step.id) },
                              { label: "Duplicate", icon: MI.duplicate, onClick: ops.duplicate },
                              "divider",
                              { label: "Move up", icon: MI.up, onClick: () => ops.move(-1), disabled: !ops.canMoveUp },
                              { label: "Move down", icon: MI.down, onClick: () => ops.move(1), disabled: !ops.canMoveDown },
                              "divider",
                              { label: step.disabled ? "Turn this step back on" : "Turn off this step", icon: MI.power, onClick: ops.toggleOff },
                              { label: "Delete", icon: MI.trash, danger: true, onClick: ops.remove },
                            ]}
                          />
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}

                {/* tail: connector + add-a-step / first-action placeholder */}
                <div className="gmba-conn" style={stagger(tailRowIndex)} />
                {!draft.steps.length ? (
                  <div className="gmba-cardwrap" style={stagger(tailRowIndex + 1)}>
                    <button
                      type="button"
                      className="gmba-placeholder"
                      style={!draft.trigger ? { opacity: 0.55, cursor: "default" } : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!draft.trigger) return;
                        const isOpen = picker?.mode === "step" && picker.index === null;
                        setPicker(isOpen ? null : { mode: "step", index: null });
                      }}
                    >
                      <span className="gmba-ic"><IconPlus size={17} /></span>
                      <span className="gmba-ctext">
                        <span className="gmba-ctag">Then do this</span>
                        <span className="gmba-ct" style={{ fontWeight: 500, color: "inherit" }}>Add an action</span>
                      </span>
                    </button>
                    {picker?.mode === "step" && picker.index === null && (
                      <StepPicker mode="step" onPick={(d) => addStep(d, null)} inline />
                    )}
                  </div>
                ) : (
                  <div className="gmba-cardwrap gmba-nonode" style={stagger(tailRowIndex + 1)}>
                    <button
                      type="button"
                      className="gmba-addstep"
                      onClick={(e) => {
                        e.stopPropagation();
                        const isOpen = picker?.mode === "step" && picker.index === null;
                        setPicker(isOpen ? null : { mode: "step", index: null });
                      }}
                    >
                      <IconPlus size={13} /> Add a step
                    </button>
                    {picker?.mode === "step" && picker.index === null && draft.steps.length > 0 && (
                      <StepPicker mode="step" onPick={(d) => addStep(d, null)} inline />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* setup panel */}
            <SetupPanel
              selected={selected}
              draft={draft}
              updateConfig={updateConfig}
              openSelect={openSelect}
              setOpenSelect={setOpenSelect}
              iconClass={iconClassFor(selected)}
              kpiOptions={kpiOptions}
              badgeOptions={badgeOptions}
              personOptions={workspaceUsers}
              issues={issues}
              workspaceName={workspaceName}
            />
          </div>
        </div>
      </Dialog>

      {/* discard-changes confirm */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <div className="gmba gmba-success" style={{ ...brandTokens(primary), width: 360, padding: "28px 28px 22px" }}>
          <div className="gmba-success-t" style={{ fontSize: 17 }}>Discard this automation?</div>
          <div className="gmba-success-d">Your changes haven't been saved.</div>
          <div className="gmba-success-row" style={{ marginTop: 20 }}>
            <button type="button" className="gmba-btn gmba-ghost" onClick={() => setConfirmClose(false)}>Keep editing</button>
            <button
              type="button"
              className="gmba-btn gmba-primary"
              style={{ background: "#B42318", borderColor: "#B42318" }}
              onClick={() => { setConfirmClose(false); onClose(); }}
            >Discard</button>
          </div>
        </div>
      </Dialog>

      {/* success */}
      <Dialog open={!!success} onClose={() => { setSuccess(null); onClose(); }} PaperProps={{ sx: { borderRadius: "20px" } }}>
        <div className="gmba gmba-success" style={{ ...brandTokens(primary) }}>
          {confetti.map((c) => (
            <span key={c.id} className="gmba-confetti" style={{ left: c.left, background: c.background, animationDelay: c.animationDelay }} />
          ))}
          <div className="gmba-badge">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div className="gmba-success-t">Automation is live! 🎉</div>
          <div className="gmba-success-d">
            <b>{success?.name || draft.name || "Your automation"}</b>
            {workspaceName ? <> is now running for <b>{workspaceName}</b>.</> : <> is now running.</>} It'll fire on its own — you don't have to lift a finger.
          </div>
          <div className="gmba-success-row">
            <button
              type="button"
              className="gmba-btn gmba-ghost"
              onClick={() => {
                setSuccess(null);
                setDraft(freshDraft());
                setSelected(null);
                setPicker({ mode: "trigger" });
                dirtyRef.current = false;
              }}
            >Build another</button>
            <button type="button" className="gmba-btn gmba-primary" onClick={() => { setSuccess(null); onClose(); }}>Done</button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
